import { RootStore } from '../RootStore'
import {
  ProductProjectViewersStat, StatsTableEntity,
  statsViewersTableInitialPaginationProps,
  StatsViewersTableSortProps
} from './StatisticsViewersTableStore.types'
import { action, computed, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { PROJECT_MAIN_STORE } from '../ProjectMainStore'
import { PieSegment, STATISTICS_PAGE_STORE, statsColorPallette } from '../StatisticsPageStore'
import { Api, getSortingResult, User } from '../../utilities'
import {
  BrandDTO,
  OrderStatsRequestDTO,
  ProductDTO,
  ProductProjectSalesDTO,
  ProjectDTO, ProjectProductStatDTO
} from '../../models'
import { PRODUCTS_MAIN_STORE } from '../ProductsMainStore'
import { GENERAL_STORE } from '../GeneralStore'
import { FilterDTO } from '../../models/FilterDTO'
import moment from 'moment'
import { STATISTICS_VIEWER_STORE } from '../StatisticsViewerStore'
import { TriggerSearchFunc } from '../PaginationSearchBase/PaginationSearchBase.types'
import {
  StatsBaseTableStore,
  StatsTableEntityObject,
  StatsTableColType,
  DraggableColLabels,
  SelectedCorrespondingEntitiesMap,
  SelectedCorrespondingEntitySets,
  getCorrespondingEntityIdKey,
  getProductProjectStatMapKey
} from '../StatsBaseTableStore'
import { STATISTICS_OVERVIEW_PERIOD_CHART_STORE } from 'store/StatisticsViewerPeriodChartStore'

export const STATISTICS_VIEWERS_TABLE_STORE = 'STATISTICS_VIEWERS_TABLE_STORE'

export class StatisticsViewersTableStore extends StatsBaseTableStore<
  StatsViewersTableSortProps,
  StatsTableEntity
> {
  private updatePeriodChartTimeout: NodeJS.Timeout = null

  constructor (rootStore: RootStore) {
    super(
      rootStore,
      statsViewersTableInitialPaginationProps
    )
  }

  private statsMap: Map<string, ProductProjectViewersStat> = new Map()

  @observable projectsCount: number = 0
  @observable getSubTableEntity = computedFn(
    (
      mainEntity: StatsTableEntityObject,
      correspondingEntity: StatsTableEntityObject,
      correspondingEntityType: StatsTableColType
    ): StatsTableEntity => {
      const correspondingEntityColLabel =
        this.getEntityColLabelByColType(correspondingEntityType)
      const correspondingEntityIdKey =
        getCorrespondingEntityIdKey(correspondingEntityColLabel)
      const mainEntityIdKey =
        getCorrespondingEntityIdKey(this.tableSettings.mainEntityColLabel)

      return {
        mainEntity: mainEntity,
        ...this.getCorrespondingEntitiesAndCalcStats(this.getStatsFor({
          [mainEntityIdKey]: mainEntity.id,
          [correspondingEntityIdKey]: correspondingEntity.id
        })),
        children: []
      }
    }
  )

  @action private setProjectsCount = (value: number): void => {
    this.projectsCount = value
  }

  private getCorrespondingEntitiesAndCalcStats = (
    stats: ProductProjectViewersStat[],
    initialStatValues?: Partial<StatsTableEntity['stats']>
  ): {
    primaryCorrespondingEntities: StatsTableEntity['primaryCorrespondingEntities'],
    primaryCorrespondingEntitiesCount: StatsTableEntity['primaryCorrespondingEntitiesCount'],
    secondaryCorrespondingEntities: StatsTableEntity['secondaryCorrespondingEntities'],
    secondaryCorrespondingEntitiesCount: StatsTableEntity['secondaryCorrespondingEntitiesCount'],
    stats: StatsTableEntity['stats'],
    bestItemPrimary: PieSegment['bestItem'],
    bestItemSecondary: PieSegment['bestItem']
  } => {
    // console.log('corresponding entities and stats:', stats, initialStatValues)
    const mainEntityIdKey = getCorrespondingEntityIdKey(
      this.tableSettings.mainEntityColLabel
    )
    const primaryCorrespondingEntityIds: Set<number> = new Set()
    const primaryCorrespondingEntityIdKey = getCorrespondingEntityIdKey(
      this.tableSettings.primaryCorrespondingEntityColLabel
    )
    const secondaryCorrespondingEntityIds: Set<number> = new Set()
    const secondaryCorrespondingEntityIdKey = getCorrespondingEntityIdKey(
      this.tableSettings.secondaryCorrespondingEntityColLabel
    )

    let totalWatch = initialStatValues?.totalWatch || 0
    let totalVovWatch = initialStatValues?.totalVovWatch || 0
    let totalInteraction = initialStatValues?.totalInteraction || 0
    let totalVovInteraction = initialStatValues?.totalVovInteraction || 0
    let totalPageInteraction = initialStatValues?.totalPageInteraction || 0
    let addExpenses = initialStatValues?.addExpenses || 0
    let purchases = initialStatValues?.purchases || 0

    const bestItemPrimary = {
      id: null,
      total: 0
    }
    const bestItemSecondary = {
      id: null,
      total: 0
    }

    // const interactionsByProject = stats.find(stat => !stat.brandId && !stat.brandName && !stat.productId && !stat.productName);
    // console.log(interactionsByProject)

    //XXX TODO here should show VoV times separated by project not only total
    const totalVovWatchTimeByProject = stats.filter(stat => stat.totalVovWatchTime).map(time => time.totalVovWatchTime).reduce((partialSum, a) => partialSum + a, 0);
    const totalVovInteractionTimeByProject = stats.filter(stat => stat.totalVovInteractionTime).map(time => time.totalVovInteractionTime).reduce((partialSum, a) => partialSum + a, 0);

    stats
      .filter(stat => {
        if (!stat[secondaryCorrespondingEntityIdKey]) {
          return true // product or brand is null
        }
        const primaryEntityIdKey =
          `${stat[mainEntityIdKey]}-${stat[primaryCorrespondingEntityIdKey]}`
        const secondaryEntityIdKey =
          `${primaryEntityIdKey}-${stat[secondaryCorrespondingEntityIdKey]}`
        // console.log('primary', primaryEntityIdKey, this.columnFilterSelections.primary)
        if (this.columnFilterSelections.primary.has(primaryEntityIdKey)) {
          return this.columnFilterSelections.secondary.has(secondaryEntityIdKey)
        } else {
          return false
        }
      })
      .forEach(stat => {
        stat[primaryCorrespondingEntityIdKey] &&
          primaryCorrespondingEntityIds.add(stat[primaryCorrespondingEntityIdKey])
        stat[secondaryCorrespondingEntityIdKey] &&
          secondaryCorrespondingEntityIds.add(stat[secondaryCorrespondingEntityIdKey])

        totalWatch += (stat.totalWatchTime || 0)
        totalVovWatch += (stat.totalVovWatchTime || 0)
        totalInteraction += (stat.totalInteractionTime || 0)
        totalVovInteraction += (stat.totalVovInteractionTime || 0)
        totalPageInteraction += (stat.totalPageInteractionTime || 0)
        // addExpenses += (stat.revenue || 0) + (stat.commission || 0)
        addExpenses += (stat.revenue || 0)
        purchases += (stat.total || 0)
      })

    const primaryCorrespondingEntities = this.getEntities(
      primaryCorrespondingEntityIdKey,
      Array.from(primaryCorrespondingEntityIds.values())
    )
    const primaryCorrespondingEntitiesCount = primaryCorrespondingEntityIds.size
    const secondaryCorrespondingEntities = this.getEntities(
      secondaryCorrespondingEntityIdKey,
      Array.from(secondaryCorrespondingEntityIds.values())
    )
    const secondaryCorrespondingEntitiesCount = secondaryCorrespondingEntityIds.size

    return {
      primaryCorrespondingEntities,
      primaryCorrespondingEntitiesCount,
      secondaryCorrespondingEntities,
      secondaryCorrespondingEntitiesCount,
      stats: {
        totalWatch,
        totalVovWatch: totalVovWatchTimeByProject,
        totalInteraction,
        totalVovInteraction: totalVovInteractionTimeByProject,
        totalPageInteraction,
        addExpenses,
        purchases
      },
      bestItemPrimary: {
        productId: primaryCorrespondingEntityIdKey === 'productId' && bestItemPrimary.id,
        productStatNumber: bestItemPrimary.total,
        isPrice: false,
        showStoreName: true
      },
      bestItemSecondary: {
        productId: secondaryCorrespondingEntityIdKey === 'productId' && bestItemSecondary.id,
        productStatNumber: bestItemSecondary.total,
        isPrice: false,
        showStoreName: true
      }
    }
  }

  @action private setSelectedCorrespondingEntitiesMap = (
    map: SelectedCorrespondingEntitiesMap
  ): void => {
    this.selectedCorrespondingEntitiesMap = map
  }

  // @action deleteEntityTotalPeriodData = (entityId: number): void => {
  //   this.totalPeriodDataMap.delete(entityId)
  // }

  @action onMainEntityCheckboxClick = (entityId: number) => {
    const {
      [STATISTICS_OVERVIEW_PERIOD_CHART_STORE]: {
        deleteEntityTotalPeriodData,
        getBestProducts,
        hasBest
      }
    } = this.rootStore

    if (this.isMainEntitySelected(entityId)) {
      this.selectedMainEntities.delete(entityId)
      deleteEntityTotalPeriodData(entityId)
      return
    }
    if (this.selectedMainEntities.size >= 6) {
      this.selectedMainEntities.forEach((_, entityId) => {
        deleteEntityTotalPeriodData(entityId)
      })
      this.setSelectedMainEntities(new Map([[entityId, statsColorPallette[0].fill]]))
    } else {
      const usedColors = [...this.selectedMainEntities.values()]
      for (const color of statsColorPallette) {
        if (!usedColors.includes(color.fill)) {
          this.selectedMainEntities.set(entityId, color.fill)
          break
        }
      }
    }

    this.triggerPeriodChartUpdate(entityId)
    if (this.isMainEntityProduct && !hasBest) {
      getBestProducts()
    }
  }

  clearEntitySelectionsAndTotalPeriodData = () => {
    this.setSelectedMainEntities(new Map())
    this.setSelectedCorrespondingEntitiesMap(new Map())
    this.rootStore[STATISTICS_OVERVIEW_PERIOD_CHART_STORE].clearTotalPeriodDataMap()
  }

  get selectedMainEntitiesIds (): number[] {
    // TODO: expect if main entity is project and apply several sets of ids if selected project has children
    return [...this.selectedMainEntities.keys()]
  }

  onColumnEntityAggregationCheckboxClick = (
    mainEntityId: number,
    correspondingEntityIds: number[],
    columnType: StatsTableColType
  ): void => {
    const isSomeCorrespondingEntitySelected: boolean = this.isSomeCorrespondingEntitySelected(
      mainEntityId,
      correspondingEntityIds,
      columnType
    )

    correspondingEntityIds.forEach(id => {
      this.onColumnEntityCheckboxClick(
        mainEntityId,
        id,
        columnType,
        {
          forceSelect: !isSomeCorrespondingEntitySelected,
          forceDeselect: isSomeCorrespondingEntitySelected
        }
      )
    })
  }

  onColumnEntityCheckboxClick = (
    mainEntityId: number,
    correspondingEntityId: number,
    columnType: StatsTableColType,
    options?: {
      forceSelect?: boolean,
      forceDeselect?: boolean
    }
  ): void => {
    // console.log({
    //   mainEntityId,
    //   correspondingEntityId,
    //   columnType
    // })

    const columnLabel = this.getEntityColLabelByColType(columnType)
    const selectedCorrespondingEntitySets: SelectedCorrespondingEntitySets =
      this.getSelectedCorrespondingEntitySets(mainEntityId)
    const isColEntitySelected: boolean = ((): boolean => {
      switch (true) {
        case options?.forceSelect: {
          return false
        }
        case options?.forceDeselect: {
          return true
        }
        default: {
          return this.isCorrespondingEntitySelected(
            mainEntityId,
            correspondingEntityId,
            columnType
          )
        }
      }
    })()

    const initialCorrespondingEntitySets: SelectedCorrespondingEntitySets =
      this.getInitialCorrespondingEntitySetsForMainEntity({ mainEntityId })

    switch (columnLabel) {
      case DraggableColLabels.PROJECT: {
        const {
          [PROJECT_MAIN_STORE]: {
            doesProjectHasChildren,
            getProjectChildrenIds
          }
        } = this.rootStore

        const deleteCorrespondingEntitiesForProject = (projectId: number) => {
          selectedCorrespondingEntitySets[DraggableColLabels.PROJECT]
            .delete(projectId)
          const selectedProjectIds = Array
            .from(selectedCorrespondingEntitySets[DraggableColLabels.PROJECT])
          this.getBrandIdsForProject(projectId).forEach(brandId => {
            const brandIsAttachedToAnotherProject = selectedProjectIds
              .some(id => Array.from(this.getBrandIdsForProject(id)).some(id => id === brandId))
            if (!brandIsAttachedToAnotherProject) {
              selectedCorrespondingEntitySets[DraggableColLabels.STORE].delete(brandId)
            }
          })
          this.getProductIdsForProject(projectId).forEach(productId => {
            const productIsAttachedToAnotherProject = selectedProjectIds
              .some(id => Array.from(this.getProductIdsForProject(id)).some(id => id === productId))
            if (!productIsAttachedToAnotherProject) {
              selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].delete(productId)
            }
          })
        }

        const addCorrespondingEntitiesForProject = (projectId: number) => {
          selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].add(projectId)
          this.getBrandIdsForProject(projectId).forEach(brandId => {
            if (initialCorrespondingEntitySets[DraggableColLabels.STORE].has(brandId)) {
              selectedCorrespondingEntitySets[DraggableColLabels.STORE].add(brandId)
            }
          })
          this.getProductIdsForProject(projectId).forEach(productId => {
            if (initialCorrespondingEntitySets[DraggableColLabels.PRODUCTS].has(productId)) {
              selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].add(productId)
            }
          })
        }

        const hasChildren = doesProjectHasChildren(correspondingEntityId)
        const childrenIds = getProjectChildrenIds(correspondingEntityId)

        if (isColEntitySelected) {
          if (hasChildren) {
            childrenIds.forEach(deleteCorrespondingEntitiesForProject)
          } else {
            deleteCorrespondingEntitiesForProject(correspondingEntityId)
          }
        } else {
          if (hasChildren) {
            childrenIds.forEach(addCorrespondingEntitiesForProject)
          } else {
            addCorrespondingEntitiesForProject(correspondingEntityId)
          }
        }

        break
      }
      case DraggableColLabels.STORE: {
        const productIds = this.getProductIdsForBrand(correspondingEntityId)
        const projectIds = this.getProjectIdsForBrand(correspondingEntityId)

        if (isColEntitySelected) {
          // apply to Brands/Stores selection
          selectedCorrespondingEntitySets[DraggableColLabels.STORE]
            .delete(correspondingEntityId)

          // apply to Projects selection
          const hasCorrespondingProjects =
            Boolean(selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].size)
          if (hasCorrespondingProjects) {
            projectIds.forEach(projectId => {
              if (
                selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].has(projectId)
              ) {
                const projectHasAnotherBrandAttached = Array
                  .from(this.getBrandIdsForProject(projectId))
                  .some(brandId =>
                    selectedCorrespondingEntitySets[DraggableColLabels.STORE].has(brandId)
                  )
                if (!projectHasAnotherBrandAttached) {
                  selectedCorrespondingEntitySets[DraggableColLabels.PROJECT]
                    .delete(projectId)
                }
              }
            })
          }

          // apply to Products selection
          productIds.forEach(productId => {
            selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].delete(productId)
          })
        } else {
          // apply to Brands/Stores selection
          selectedCorrespondingEntitySets[DraggableColLabels.STORE]
            .add(correspondingEntityId)

          // apply to Products selection
          productIds.forEach(productId => {
            if (initialCorrespondingEntitySets[DraggableColLabels.PRODUCTS].has(productId)) {
              selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].add(productId)
            }
          })

          // apply to Projects selection
          projectIds.forEach(projectId => {
            if (initialCorrespondingEntitySets[DraggableColLabels.PROJECT].has(projectId)) {
              selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].add(projectId)
            }
          })
        }
        break
      }
      case DraggableColLabels.PRODUCTS: {
        const {
          [PRODUCTS_MAIN_STORE]: {
            getProductBrandId
          }
        } = this.rootStore
        const brandId = getProductBrandId(correspondingEntityId)
        const projectIds = this.getProjectIdsForProduct(correspondingEntityId)

        if (isColEntitySelected) {
          // apply to Products selection
          selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS]
            .delete(correspondingEntityId)

          // apply to Brands/Stores selection
          const brandHasAnotherSelectedProduct = Array.from(this.getProductIdsForBrand(brandId))
            .some(productId =>
              selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].has(productId)
            )
          if (!brandHasAnotherSelectedProduct) {
            selectedCorrespondingEntitySets[DraggableColLabels.STORE].delete(brandId)
          }

          // apply to Projects selection
          projectIds.forEach(projectId => {
            const isProjectSelected =
              selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].has(projectId)
            if (isProjectSelected) {
              const projectHasAnotherProductAttached = Array
                .from(this.getProductIdsForProject(projectId))
                .some(productId =>
                  selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].has(productId)
                )
              if (!projectHasAnotherProductAttached) {
                selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].delete(projectId)
              }
            }
          })
        } else {
          // apply to Products selection
          selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS]
            .add(correspondingEntityId)

          // apply to Brands/Store selection
          selectedCorrespondingEntitySets[DraggableColLabels.STORE]
            .add(brandId)

          // apply to Projects selection
          projectIds.forEach(projectId => {
            const isProjectACorrespondingEntity =
              initialCorrespondingEntitySets[DraggableColLabels.PROJECT].has(projectId)
            if (isProjectACorrespondingEntity) {
              selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].add(projectId)
            }
          })
        }
        break
      }
      default:
        break
    }

    this.setSelectedCorrespondingEntities(mainEntityId, selectedCorrespondingEntitySets)
    if (this.isMainEntitySelected(mainEntityId)) {
      this.triggerPeriodChartUpdate(mainEntityId)
    }
  }

  @action private setSelectedCorrespondingEntities = (
    mainEntityId: number,
    sets: SelectedCorrespondingEntitySets
  ): void => {
    this.selectedCorrespondingEntitiesMap.set(mainEntityId, sets)
  }

  triggerPeriodChartUpdate = (mainEntityId?: number): void => {
    const {
      [STATISTICS_OVERVIEW_PERIOD_CHART_STORE]: {
        getProjectPeriodStats,
        getProductPeriodStats
      }
    } = this.rootStore

    if (this.updatePeriodChartTimeout) {
      clearTimeout(this.updatePeriodChartTimeout)
    }

    const getStats = this.isMainEntityProject
      ? () => mainEntityId
        ? getProjectPeriodStats(this.getParentId(mainEntityId))
        : Promise.all(this.selectedMainEntitiesIds.map(getProjectPeriodStats))
      : () => mainEntityId
        ? getProductPeriodStats(mainEntityId)
        : Promise.all(this.selectedMainEntitiesIds.map(getProductPeriodStats))

    this.updatePeriodChartTimeout = setTimeout(getStats, 300)
  }

  private getInitialCorrespondingEntitySetsForMainEntity = (data: {
    mainEntityId?: number,
    tableEntity?: StatsTableEntity
  }): SelectedCorrespondingEntitySets => {
    const tableEntity = data.tableEntity ||
      this.searchResults.find(r => r.mainEntity.id === this.getParentId(data.mainEntityId))
    if (!tableEntity) return null

    const mainEntityId = tableEntity.mainEntity.id

    const correspondingEntitySets: SelectedCorrespondingEntitySets = {
      [DraggableColLabels.PROJECT]: this.isMainEntityProject
        ? this.rootStore[PROJECT_MAIN_STORE].getProjectChildrenIds(mainEntityId)
        : new Set(),
      // [DraggableColLabels.PROJECT]: new Set(),
      [DraggableColLabels.STORE]: new Set(),
      [DraggableColLabels.PRODUCTS]: new Set()
    }

    const {
      primaryCorrespondingEntityColLabel,
      secondaryCorrespondingEntityColLabel
    } = this.tableSettings

    correspondingEntitySets[primaryCorrespondingEntityColLabel] =
      new Set(tableEntity.primaryCorrespondingEntities.map(e => e.id))

    if (secondaryCorrespondingEntityColLabel) {
      correspondingEntitySets[secondaryCorrespondingEntityColLabel] =
        new Set(tableEntity.secondaryCorrespondingEntities.map(e => e?.id))
    }

    return correspondingEntitySets
  }

  getViewersTableData = (): void => {
    const {
      [GENERAL_STORE]: {
        setLoadingOff,
        handleApiError
      },
      [STATISTICS_PAGE_STORE]: {
        selectedStoreIds
      },
      [STATISTICS_VIEWER_STORE]: {
        period
      }
    } = this.rootStore

    Promise.all([
      Api.post<ProjectProductStatDTO[], FilterDTO>(
        '/stats/summary/projectProduct',
        {
          ...period,
          brandIds: selectedStoreIds,
          brandId: null,
          projectIds: null // if projectIds than brandId != null
        },
        'statsApi'
      ),
      Api.post<ProductProjectSalesDTO[], OrderStatsRequestDTO>(
        '/order/statistic/orderProduct',
        {
          startDate: moment(period.startDate != null ? period.startDate : moment()).format('YYYY-MM-DD'),
          endDate: moment(period.endDate != null ? period.endDate : moment()).format('YYYY-MM-DD'),
          brands: selectedStoreIds
        }
      )
    ])
      .then(data => {
        this.handleProjectProductData(data)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  tableSortingCompareFn = (a: StatsTableEntity, b: StatsTableEntity): number => {
    const {
      sortProp,
      sortDirection
    } = this.pagination

    switch (sortProp) {
      case StatsViewersTableSortProps.TOTAL_WATCH: {
        const totalA = (a.stats.totalWatch + a.stats.totalVovWatch)
        const totalB = (b.stats.totalWatch + b.stats.totalVovWatch)

        return getSortingResult(totalA, totalB, sortDirection)
      }
      case StatsViewersTableSortProps.TOTAL_INTERACTION: {
        const totalA = (a.stats.totalInteraction + a.stats.totalPageInteraction + a.stats.totalVovInteraction)
        const totalB = (b.stats.totalInteraction + b.stats.totalPageInteraction + b.stats.totalVovInteraction)

        return getSortingResult(totalA, totalB, sortDirection)
      }
      case StatsViewersTableSortProps.ADD_EXPENSES:
      case StatsViewersTableSortProps.ADD_REVENUE: {
        return getSortingResult(a.stats.addExpenses, b.stats.addExpenses, sortDirection)
      }
      case StatsViewersTableSortProps.PURCHASE_REVENUE: {
        return getSortingResult(a.stats.purchases, b.stats.purchases, sortDirection)
      }
      default: return 0
    }
  }

  triggerSearch: TriggerSearchFunc = (options): void => {
    this.setProcessing(true)
    const isEntitySelected = (entity: StatsTableEntityObject): boolean => {
      const {
        children
      } = entity as ProjectDTO

      const hasChildren = Boolean(children && children.length)

      if (hasChildren) {
        return children.some(isEntitySelected)
      } else {
        return this.columnFilterSelections.main.has(entity.id)
      }
    }

    const parseProjectToTableEntity = (project: ProjectDTO): StatsTableEntity => {
      const children = (project.children || [])
        .filter(isEntitySelected)
        .map(parseProjectToTableEntity)

      const statsAndCorrespondingEntities = this.getCorrespondingEntitiesAndCalcStats(
        this.getStatsFor({ projectId: project.id })
      )
      // console.log('stats for project: ', project.id, statsAndCorrespondingEntities)

      let primaryCorrespondingEntitiesSet = new Set(
        statsAndCorrespondingEntities.primaryCorrespondingEntities || []
      )
      let secondaryCorrespondingEntitiesSet = new Set(
        statsAndCorrespondingEntities.secondaryCorrespondingEntities || []
      )

      children.forEach(tableEntity => {
        primaryCorrespondingEntitiesSet = new Set([
          ...primaryCorrespondingEntitiesSet,
          ...tableEntity.primaryCorrespondingEntities
        ])
        secondaryCorrespondingEntitiesSet = new Set([
          ...secondaryCorrespondingEntitiesSet,
          ...tableEntity.secondaryCorrespondingEntities
        ])
      })

      const primaryCorrespondingEntitiesCount = primaryCorrespondingEntitiesSet.size
      const primaryCorrespondingEntities = [...primaryCorrespondingEntitiesSet.values()]
      const secondaryCorrespondingEntitiesCount = secondaryCorrespondingEntitiesSet.size
      const secondaryCorrespondingEntities = [...secondaryCorrespondingEntitiesSet.values()]

      return {
        mainEntity: project,
        ...statsAndCorrespondingEntities,
        primaryCorrespondingEntities,
        primaryCorrespondingEntitiesCount,
        secondaryCorrespondingEntities,
        secondaryCorrespondingEntitiesCount,
        children
      }
    }

    const parseBrandToTableEntity = (brand: BrandDTO): StatsTableEntity => {
      return {
        mainEntity: brand,
        ...this.getCorrespondingEntitiesAndCalcStats(this.getStatsFor({ brandId: brand.id }))
      }
    }

    const parseProductToTableEntity = (product: ProductDTO): StatsTableEntity => {
      return {
        mainEntity: product,
        ...this.getCorrespondingEntitiesAndCalcStats(this.getStatsFor({
          productId: product.id
        }))
      }
    }

    // console.log('table settings: ', this.tableSettings.mainEntityColLabel, this.tableProjects)

    const results: StatsTableEntity[] = (() => {
      switch (this.tableSettings.mainEntityColLabel) {
        case DraggableColLabels.PROJECT: {
          return this.tableProjects
            .filter(isEntitySelected)
            .map(parseProjectToTableEntity)
        }
        case DraggableColLabels.STORE: {
          return this.tableBrands
            .filter(isEntitySelected)
            .map(parseBrandToTableEntity)
        }
        case DraggableColLabels.PRODUCTS: {
          return this.tableProducts
            .filter(isEntitySelected)
            .map(parseProductToTableEntity)
        }
      }
    })()

    // console.log('viewes table result', results)

    const {
      page: currentPagePaginationSetting,
      size
    } = this.pagination

    const page = options?.resetPageNumber ? 0 : currentPagePaginationSetting

    results.sort(this.tableSortingCompareFn)

    this.setPaginationProps({
      page: options?.resetPageNumber ? 0 : page,
      totalElements: results.length,
      totalPages: Math.ceil(results.length / size)
    })
    this.updateSelectedCorrespondingEntities(results)

    const startIndex = page * size
    const endIndex = startIndex + size
    // console.log('viewes table result', results)
    this.setSearchResults(results.slice(startIndex, endIndex))
    this.setProcessing(false)
  }

  getStatsFor = (
    searchBy: { projectId?: number, productId?: number, brandId?: number }
  ): ProductProjectViewersStat[] => {
    const {
      projectId,
      productId,
      brandId
    } = searchBy

    let mapKeysSet: Set<string> = new Set()
    if (projectId) {
      const {
        [PROJECT_MAIN_STORE]: {
          doesProjectHasChildren,
          getProjectChildrenIds
        }
      } = this.rootStore

      const addMapKeysForProject = (id: number) => {
        mapKeysSet = new Set([
          ...mapKeysSet,
          ...this.statsMapKeysByProject.get(id) || new Set()
        ])

        if (doesProjectHasChildren(id)) {
          getProjectChildrenIds(id).forEach(addMapKeysForProject)
        }
      }

      addMapKeysForProject(projectId)
    }
    if (productId) {
      const mapKeysForProduct = this.statsMapKeysByProduct.get(productId)
      if (mapKeysSet.size) {
        mapKeysSet = new Set([
          ...[...mapKeysSet].filter(mapKey => mapKeysForProduct.has(mapKey))
        ])
      } else {
        mapKeysSet = mapKeysForProduct
      }
    }
    if (brandId) {
      const mapKeysForBrand = this.statsMapKeysByBrand.get(brandId)
      if (mapKeysSet.size) {
        mapKeysSet = new Set([
          ...[...mapKeysSet].filter(mapKey => mapKeysForBrand.has(mapKey))
        ])
      } else {
        mapKeysSet = mapKeysForBrand
      }
    }

    const mapKeys: string[] = Array.from((mapKeysSet || new Set()).values())

    return mapKeys.map(mapKey => this.statsMap.get(mapKey))
  }

  private updateSelectedCorrespondingEntities = (
    tableEntities: StatsTableEntity[]
  ): void => {
    const isMainEntityColChanged = this.selectedCorrespondingEntitiesMap.size === 0
    if (!isMainEntityColChanged) return

    const selectedCorrespondingEntitiesMap: SelectedCorrespondingEntitiesMap = new Map()

    tableEntities.forEach(tableEntity => {
      selectedCorrespondingEntitiesMap.set(
        tableEntity.mainEntity.id,
        this.getInitialCorrespondingEntitySetsForMainEntity({ tableEntity })
      )
    })
    this.setSelectedCorrespondingEntitiesMap(selectedCorrespondingEntitiesMap)
  }

  private handleProjectProductData = (data: [ProjectProductStatDTO[], ProductProjectSalesDTO[]]): void => {
    const [eventStats, sales] = data
    const handleEachEntity = (entity: ProjectProductStatDTO | ProductProjectSalesDTO) => {
      const mapKey = getProductProjectStatMapKey(entity) // `${projectId}-${productId}-${brandId}`

      // add entity to stats Map catalog or update existing ProductProjectStat
      this.statsMap.set(mapKey, {
        ...this.statsMap.get(mapKey),
        ...entity
      })

      // add ProductProjectStat mapKey to its project Set of mapKeys
      if (entity.projectId) {
        this.statsMapKeysByProject.set(
          entity.projectId,
          this.statsMapKeysByProject.get(entity.projectId) || new Set()
        )
        this.statsMapKeysByProject.get(entity.projectId).add(mapKey)
      }

      // add ProductProjectStat mapKey to its product Set of mapKeys
      if (entity.productId) {
        this.statsMapKeysByProduct.set(
          entity.productId,
          this.statsMapKeysByProduct.get(entity.productId) || new Set()
        )
        this.statsMapKeysByProduct.get(entity.productId).add(mapKey)
      }

      // add ProductProjectStat mapKey to its brand Set of mapKeys
      if (entity.brandId) {
        this.statsMapKeysByBrand.set(
          entity.brandId,
          this.statsMapKeysByBrand.get(entity.brandId) || new Set()
        )
        this.statsMapKeysByBrand.get(entity.brandId).add(mapKey)
      }
    }

    // init
    this.statsMap.clear()
    this.statsMapKeysByProject.clear()
    this.statsMapKeysByProduct.clear()
    this.statsMapKeysByBrand.clear()
    this.statsProjectIds.length = 0
    this.statsProductIds.length = 0
    this.setProjectsCount(0)
    // end of init

    eventStats.forEach(handleEachEntity)
    sales.forEach(handleEachEntity)
    this.setProjectsCount(this.statsMapKeysByProject.size)
    // console.log('viewers table: ', this.statsMap, this.statsMapKeysByProduct, this.statsMapKeysByProject)

    // this.setStatsMap(statsMap)
    // this.setProjectProductStatsMap(projectProductStatsMap)
    // this.setProductProjectStatsMap(productProjectStatsMap)

    const {
      [PROJECT_MAIN_STORE]: {
        getProjectsByIds
      },
      [PRODUCTS_MAIN_STORE]: {
        getProducts
      }
    } = this.rootStore

    // clearProjects()
    // clearProjectHierarchyList()

    Promise.all([
      getProjectsByIds(this.statsProjectIds, {
        loadContents: true,
        loadHierarchy: true
      }),
      getProducts({ ids: this.statsProductIds })
    ])
      .then(() => {
        // set all main entities to be selected in the table tree-view dropdown filter
        this.refreshColumnFilterSelections()
        this.triggerSearch({ resetPageNumber: true })
      })
  }

  setSingleStoreView () {
    this.init()
    this.setTableSetting({
      draggableColumnsCount: 2,
      mainEntityColLabel: DraggableColLabels.PROJECT,
      primaryCorrespondingEntityColLabel: DraggableColLabels.PRODUCTS,
      secondaryCorrespondingEntityColLabel: DraggableColLabels.STORE
    })
    //  this.rootStore[STATISTICS_OVERVIEW_STORE].clearTotalPeriodDataMap()
  }

  setMultiStoreView () {
    this.setTableSetting({
      draggableColumnsCount: 3,
      mainEntityColLabel: DraggableColLabels.STORE,
      primaryCorrespondingEntityColLabel: DraggableColLabels.PROJECT,
      secondaryCorrespondingEntityColLabel: DraggableColLabels.PRODUCTS
    })
    this.init()
    // this.rootStore[STATISTICS_OVERVIEW_STORE].clearTotalPeriodDataMap()
  }

  public get addExpensesColLabel (): StatsViewersTableSortProps {
    return User.isShopOwner()
      ? StatsViewersTableSortProps.ADD_EXPENSES
      : StatsViewersTableSortProps.ADD_REVENUE
  }

  @computed get isTotalWatchTimeColDisabled (): boolean {
    return this.tableSettings.mainEntityColLabel === DraggableColLabels.PRODUCTS
  }
}
